import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { searchItems } from "@/lib/search";
import { inr } from "@/lib/format";
import Modal, { Field, inputCls, btnPrimary, btnGhost } from "@/components/Modal";
import { Plus, Search, Pencil, Trash2, Leaf, Drumstick, X } from "lucide-react";

const empty = {
  name: "", category_id: "", short_code: "", keywords: [], price: 0,
  gst_percent: 5, veg: true, available: true, description: "", image_url: "", variations: [],
};

export default function MenuManagement() {
  const { categories, loadMenu } = useData();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [kwText, setKwText] = useState("");

  const load = async () => {
    const { data } = await api.get("/menu");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (cat !== "ALL") list = list.filter((i) => i.category_id === cat);
    return searchItems(list, q);
  }, [items, q, cat]);

  const openNew = () => {
    setEdit(null);
    setForm({ ...empty, gst_percent: 5 });
    setKwText("");
    setOpen(true);
  };
  const openEdit = (it) => {
    setEdit(it);
    setForm({ ...empty, ...it });
    setKwText((it.keywords || []).join(", "));
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      gst_percent: parseFloat(form.gst_percent) || 0,
      keywords: kwText.split(",").map((k) => k.trim()).filter(Boolean),
      category_id: form.category_id || null,
      variations: form.variations.map((v) => ({ name: v.name, price: parseFloat(v.price) || 0 })),
    };
    try {
      if (edit) await api.put(`/menu/${edit.id}`, payload);
      else await api.post("/menu", payload);
      toast.success("Saved");
      setOpen(false);
      await load();
      await loadMenu();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const del = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try {
      await api.delete(`/menu/${it.id}`);
      toast.success("Deleted");
      await load();
      await loadMenu();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const addVar = () => setForm((f) => ({ ...f, variations: [...f.variations, { name: "", price: 0 }] }));
  const setVar = (i, k, v) => setForm((f) => {
    const vs = [...f.variations];
    vs[i] = { ...vs[i], [k]: v };
    return { ...f, variations: vs };
  });
  const rmVar = (i) => setForm((f) => ({ ...f, variations: f.variations.filter((_, idx) => idx !== i) }));

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-head text-2xl font-bold">Menu Management</h1>
          <p className="text-sm text-[#6B7280]">{items.length} items</p>
        </div>
        <button data-testid="add-item-btn" onClick={openNew} className={btnPrimary + " flex items-center gap-2"}>
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#6B7280] absolute left-3 top-1/2 -translate-y-1/2" />
          <input className={inputCls + " pl-9"} placeholder="Search items…" value={q} data-testid="menu-search" onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className={inputCls + " w-auto"} value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="ALL">All Categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="bg-[#151D2A] border border-[#23334D] rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 px-4 py-2.5 text-xs font-semibold text-[#6B7280] uppercase tracking-wide border-b border-[#23334D]">
          <div>Item</div><div>Code</div><div className="text-right">Price</div><div className="text-center">GST</div><div></div>
        </div>
        <div className="divide-y divide-[#23334D] max-h-[62vh] overflow-y-auto">
          {filtered.map((it) => (
            <div key={it.id} data-testid={`menu-item-${it.id}`} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 px-4 py-3 items-center hover:bg-[#1E2C45]/40">
              <div className="flex items-center gap-2 min-w-0">
                {it.veg ? <Leaf className="w-3.5 h-3.5 text-[#10B981] shrink-0" /> : <Drumstick className="w-3.5 h-3.5 text-[#EF4444] shrink-0" />}
                <div className="min-w-0">
                  <div className="font-medium truncate">{it.name}</div>
                  <div className="text-xs text-[#6B7280]">{it.category_name || "—"}{!it.available && <span className="text-[#EF4444] ml-2">Out of stock</span>}{it.variations?.length > 0 && <span className="ml-2 text-[#06B6D4]">{it.variations.length} variations</span>}</div>
                </div>
              </div>
              <div className="font-mono text-xs text-[#9CA3AF]">{it.short_code || "—"}</div>
              <div className="text-right font-mono font-semibold">{inr(it.price)}</div>
              <div className="text-center text-sm text-[#9CA3AF]">{it.gst_percent}%</div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(it)} data-testid={`edit-item-${it.id}`} className="p-2 text-[#9CA3AF] hover:text-white"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(it)} data-testid={`delete-item-${it.id}`} className="p-2 text-[#9CA3AF] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-8 text-center text-[#6B7280]">No items found.</div>}
        </div>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={edit ? "Edit Item" : "New Item"}
        width="max-w-2xl"
        footer={
          <>
            <button onClick={() => setOpen(false)} className={btnGhost}>Cancel</button>
            <button onClick={save} data-testid="save-item-btn" className={btnPrimary}>Save Item</button>
          </>
        }
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Field label="Item Name"><input className={inputCls} value={form.name} data-testid="item-name-input" onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></Field></div>
          <Field label="Category">
            <select className={inputCls} value={form.category_id || ""} data-testid="item-category-input" onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
              <option value="">— None —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Short Code"><input className={inputCls} value={form.short_code} data-testid="item-code-input" onChange={(e) => setForm({ ...form, short_code: e.target.value.toUpperCase() })} placeholder="CBM" /></Field>
          <Field label="Price (₹)"><input type="number" className={inputCls} value={form.price} data-testid="item-price-input" onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
          <Field label="GST %"><input type="number" className={inputCls} value={form.gst_percent} data-testid="item-gst-input" onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} /></Field>
          <div className="sm:col-span-2"><Field label="Search Keywords (comma separated)"><input className={inputCls} value={kwText} onChange={(e) => setKwText(e.target.value)} placeholder="butter chicken, cbm, chicken" /></Field></div>
          <Field label="Type">
            <div className="flex gap-2">
              <button type="button" onClick={() => setForm({ ...form, veg: true })} className={`flex-1 py-2.5 rounded-lg text-sm font-medium ${form.veg ? "bg-[#10B981] text-[#06221a]" : "bg-[#0B0F17] border border-[#23334D] text-[#9CA3AF]"}`}>Veg</button>
              <button type="button" onClick={() => setForm({ ...form, veg: false })} className={`flex-1 py-2.5 rounded-lg text-sm font-medium ${!form.veg ? "bg-[#EF4444] text-white" : "bg-[#0B0F17] border border-[#23334D] text-[#9CA3AF]"}`}>Non-Veg</button>
            </div>
          </Field>
          <Field label="Availability">
            <div className="flex gap-2">
              <button type="button" onClick={() => setForm({ ...form, available: true })} className={`flex-1 py-2.5 rounded-lg text-sm font-medium ${form.available ? "bg-[#10B981] text-[#06221a]" : "bg-[#0B0F17] border border-[#23334D] text-[#9CA3AF]"}`}>Available</button>
              <button type="button" onClick={() => setForm({ ...form, available: false })} className={`flex-1 py-2.5 rounded-lg text-sm font-medium ${!form.available ? "bg-[#F59E0B] text-[#241a02]" : "bg-[#0B0F17] border border-[#23334D] text-[#9CA3AF]"}`}>Out of Stock</button>
            </div>
          </Field>
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide">Variations</label>
              <button type="button" onClick={addVar} data-testid="add-variation-btn" className="text-xs text-[#10B981] flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
            </div>
            <div className="space-y-2">
              {form.variations.map((v, i) => (
                <div key={i} className="flex gap-2">
                  <input className={inputCls} placeholder="Name (e.g. Small)" value={v.name} onChange={(e) => setVar(i, "name", e.target.value)} />
                  <input type="number" className={inputCls + " w-28"} placeholder="Price" value={v.price} onChange={(e) => setVar(i, "price", e.target.value)} />
                  <button type="button" onClick={() => rmVar(i)} className="px-3 text-[#EF4444]"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
